import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAuthStore from "../../../store/useAuthStore";
import RequisitionTimeline from "./Components/RequisitionTimeline";
import RequisitionStepper from "./Components/RequisitionStepper";
import { REQUISITION_STATUSES, PRIORITY_LEVELS } from "../../../constants/inventory";
import { IoArrowBack } from "react-icons/io5";
import { Skeleton } from "@mui/material";

const ViewRequisition = () => {
  const { token, user } = useAuthStore();
  const navigate = useNavigate();
  const { id } = useParams();

  const [requisition, setRequisition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const isOwner = user?.role === "Owner";
  const isReviewManager = user?.role === "Manager";
  const isManager = ["Owner", "Accounts", "StoreIncharge"].includes(user?.role);

  const fetchRequisition = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/getRequisition/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) {
        setRequisition(data.requisition);
      } else {
        setErrorMessage(data.error || "Failed to fetch requisition");
      }
    } catch (err) {
      setErrorMessage("Failed to fetch requisition");
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  useEffect(() => {
    fetchRequisition();
  }, [fetchRequisition]);

  const handleAction = async (action, body = {}) => {
    setActionLoading(true);
    try {
      const response = await fetch(`/api/requisition/${action}/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccessMessage(`Requisition ${action}d successfully`);
        setRequisition(data.requisition);
        setShowRejectModal(false);
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setErrorMessage(data.error || `Failed to ${action} requisition`);
        setTimeout(() => setErrorMessage(""), 3000);
      }
    } catch (err) {
      setErrorMessage(`Failed to ${action} requisition`);
      setTimeout(() => setErrorMessage(""), 3000);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = REQUISITION_STATUSES[status] || { label: status, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = PRIORITY_LEVELS.find((p) => p.id === priority);
    if (!config) return priority;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.name}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6">
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
      </div>
    );
  }

  if (!requisition) {
    return (
      <div className="m-2 md:m-6 p-2 md:p-6 text-center">
        <p className="text-gray-500">Requisition not found</p>
      </div>
    );
  }

  return (
    <div className="m-2 md:m-6 p-2 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/Requisitions")}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <IoArrowBack className="text-xl text-primary" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {requisition.requisitionNumber}
            </h2>
            <p className="text-sm text-gray-500">
              Created {new Date(requisition.createdAt).toLocaleDateString()}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge(requisition.status)}
          {getPriorityBadge(requisition.priority)}
        </div>
      </div>

      {/* Progress Stepper */}
      <RequisitionStepper status={requisition.status} />

      {/* Messages */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
          {successMessage}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Requester Info */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Requester Information</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Requested By</p>
                <p className="font-medium">{requisition.requestedBy?.name || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Employee ID</p>
                <p className="font-medium">{requisition.requestedBy?.employeeId || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Department</p>
                <p className="font-medium">{requisition.department?.deptName || "N/A"}</p>
              </div>
              <div>
                <p className="text-gray-500">Project</p>
                <p className="font-medium">
                  {requisition.project
                    ? `${requisition.project.projectCode} - ${requisition.project.name}`
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Requested Items</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">#</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Item</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Qty</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Fulfilled</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Unit Cost</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Total</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Purpose</th>
                  </tr>
                </thead>
                <tbody>
                  {requisition.items?.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-4 py-3 text-sm">{index + 1}</td>
                      <td className="px-4 py-3 text-sm">
                        <div>
                          <p className="font-medium">{item.item?.name || "N/A"}</p>
                          <p className="text-xs text-gray-400">{item.item?.sku || ""}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">{item.qty}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={item.fulfilledQty >= item.qty ? "text-green-600 font-medium" : ""}>
                          {item.fulfilledQty || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">Nu. {item.unitCostAtTime || item.item?.unitCost || 0}</td>
                      <td className="px-4 py-3 text-sm font-medium">
                        Nu. {((item.unitCostAtTime || item.item?.unitCost || 0) * item.qty).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">{item.purpose || "-"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan={5} className="px-4 py-3 text-sm font-semibold text-right">
                      Estimated Total:
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-primary">
                      Nu. {requisition.totalEstimatedCost?.toLocaleString() || "0"}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Notes */}
          {requisition.notes && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">Notes</h3>
              <p className="text-sm text-gray-700">{requisition.notes}</p>
            </div>
          )}

          {/* Rejection reasons */}
          {requisition.managerRejectionReason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-red-700 uppercase mb-2">Rejected by Manager</h3>
              <p className="text-sm text-red-600">{requisition.managerRejectionReason}</p>
            </div>
          )}
          {requisition.rejectionReason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-5">
              <h3 className="text-sm font-semibold text-red-700 uppercase mb-2">Rejection Reason</h3>
              <p className="text-sm text-red-600">{requisition.rejectionReason}</p>
            </div>
          )}
        </div>

        {/* Right: Timeline + Actions */}
        <div className="space-y-6">
          {/* Manager Review Actions */}
          {isReviewManager && requisition.status === "pending_manager_approval" && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Manager Review</h3>
              <p className="text-xs text-gray-400 mb-3">
                Review this requisition and forward to Owner, or reject it.
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleAction("manager-approve")}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50"
                >
                  {actionLoading ? "Processing..." : "Forward to Owner"}
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            </div>
          )}

          {/* Owner Approval Actions */}
          {isOwner && requisition.status === "pending_owner_approval" && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Actions</h3>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleAction("approve")}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 disabled:opacity-50"
                >
                  {actionLoading ? "Processing..." : "Approve"}
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  disabled={actionLoading}
                  className="w-full px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 disabled:opacity-50"
                >
                  Reject
                </button>
              </div>
            </div>
          )}

          {/* Submit action for draft */}
          {requisition.status === "draft" &&
            (isOwner || requisition.requestedBy?._id === user?._id) && (
              <div className="bg-white rounded-lg shadow p-5">
                <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Actions</h3>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => handleAction("submit")}
                    disabled={actionLoading}
                    className="w-full px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
                  >
                    {actionLoading ? "Submitting..." : "Submit for Approval"}
                  </button>
                  <button
                    onClick={() => navigate(`/Requisitions/Edit/${id}`)}
                    className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50"
                  >
                    Edit
                  </button>
                </div>
              </div>
            )}

          {/* Fulfillment action for approved */}
          {isManager && ["approved", "processing", "partially_fulfilled"].includes(requisition.status) && (
            <div className="bg-white rounded-lg shadow p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Fulfillment</h3>
              <button
                onClick={() => {
                  const fulfillItems = requisition.items
                    .filter((i) => i.fulfilledQty < i.qty)
                    .map((i) => ({
                      item: i.item._id || i.item,
                      qty: i.qty - i.fulfilledQty,
                    }));
                  handleAction("fulfill", { items: fulfillItems });
                }}
                disabled={actionLoading}
                className="w-full px-4 py-2 bg-indigo-500 text-white rounded-lg text-sm font-medium hover:bg-indigo-600 disabled:opacity-50"
              >
                {actionLoading ? "Fulfilling..." : "Fulfill All Remaining"}
              </button>
            </div>
          )}

          {/* Timeline */}
          <div className="bg-white rounded-lg shadow p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-4">Activity Timeline</h3>
            <RequisitionTimeline auditTrail={requisition.auditTrail} />
          </div>
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Reject Requisition</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-4"
              placeholder="Reason for rejection (required)"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => { setShowRejectModal(false); setRejectReason(""); }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!rejectReason.trim()) return;
                  const action = isReviewManager ? "manager-reject" : "reject";
                  handleAction(action, { rejectionReason: rejectReason });
                }}
                disabled={!rejectReason.trim() || actionLoading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm disabled:opacity-50"
              >
                {actionLoading ? "Rejecting..." : "Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewRequisition;
